import { useEffect, useRef, useState } from 'react';

/**
 * CalendarEmbed — Morgan Legal Group
 *
 * Renders the SAME scheduler production WordPress uses: Calendly
 * `russel-morgan/30min`. That is not a cosmetic choice — the primary Google Ads
 * conversion (AW-783494786 / anlVCIOW_JgbEILdzPUC) fires on the dataLayer event
 * `calendly.event_scheduled`, which a GTM custom-HTML tag derives from the
 * postMessage Calendly emits. Any other scheduler books the client silently and
 * reports zero conversions to Ads.
 *
 * `provider="os"` keeps the Legal-OS scheduler available for pages that want it,
 * but it is NOT conversion-tracked and its booking flow depends on the OS Google
 * OAuth app, which is still unverified.
 *
 * `embed_domain` must match the host actually serving the page or Calendly
 * renders its error screen. The initial src therefore hardcodes the production
 * host: the prerenderer executes effects, so reading window.location during the
 * build baked `embed_domain=127.0.0.1` into the static HTML of every page that
 * carries a scheduler. The effect below only corrects the host when the page is
 * NOT served from production (staging, local dev).
 */
const CALENDLY_BASE = 'https://calendly.com/russel-morgan/30min';
const OS_SRC = 'https://mlg.nygot.com/embed/calendar?primaryColor=c5a059';
const PROD_HOST = 'www.morganlegalny.com';

interface CalendarEmbedProps {
  title?: string;
  height?: number;
  className?: string;
  provider?: 'calendly' | 'os';
  /**
   * How the 2.7MB Calendly bundle is allowed to load.
   *  - 'lazy'   (default) — mounts when the embed scrolls into view.
   *  - 'facade' — mounts only after the visitor asks to book. Use above the
   *    fold: an eager embed there cost the homepage 2.7MB before its LCP.
   */
  loading?: 'lazy' | 'facade';
}

function calendlySrc(host: string): string {
  const params = new URLSearchParams({
    hide_gdpr_banner: '1',
    primary_color: 'c5a059',
    embed_domain: host,
    embed_type: 'Inline',
  });
  return `${CALENDLY_BASE}?${params.toString()}`;
}

export default function CalendarEmbed({
  title = 'Morgan Legal Group — book your free consultation',
  height = 720,
  className = '',
  provider = 'calendly',
  loading = 'lazy',
}: CalendarEmbedProps) {
  const [src, setSrc] = useState(provider === 'os' ? OS_SRC : calendlySrc(PROD_HOST));
  const [mounted, setMounted] = useState(loading !== 'facade');
  // In facade mode the visitor's click is the trigger, so viewport state is
  // irrelevant; in lazy mode the observer flips this on approach.
  const [visible, setVisible] = useState(loading === 'facade');
  const holder = useRef<HTMLDivElement>(null);

  // Keep the scheduler out of the initial page load. Prerendered HTML ships the
  // placeholder; the iframe appears when it is actually near the viewport.
  useEffect(() => {
    if (loading === 'facade' || !holder.current) return;
    const node = holder.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading]);

  useEffect(() => {
    if (provider === 'os') {
      setSrc(OS_SRC);
      return;
    }
    // The prerenderer is a headless browser on 127.0.0.1 and its effects DO run,
    // so a bare window.location read bakes the loopback host into static HTML.
    // Loopback hosts keep the production value; every other host is real.
    const host = window.location.hostname;
    const isLoopback = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
    setSrc(calendlySrc(!host || isLoopback ? PROD_HOST : host));
  }, [provider]);

  if (!mounted || !visible) {
    return (
      <div
        ref={holder}
        className={`flex w-full flex-col items-center justify-center gap-4 bg-white px-6 text-center ${className}`}
        style={{ height }}
      >
        <p className="font-display text-lg font-semibold text-navy">Schedule Your Consultation</p>
        <p className="font-body text-sm leading-relaxed text-navy/70">
          Pick a time with our team — most consultations are booked within 48 hours.
        </p>
        <button type="button" className="btn-gold" onClick={() => setMounted(true)}>
          Open the calendar
        </button>
        <a href="tel:8885291315" className="font-body text-sm font-semibold text-navy hover:text-gold">
          or call (888) 529-1315
        </a>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      title={title}
      width="100%"
      height={height}
      loading="lazy"
      allow="clipboard-write"
      className={`block w-full border-0 ${className}`}
      style={{ minHeight: 0 }}
    />
  );
}
